import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, Compass, Heart, DollarSign, Briefcase, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [gender, setGender] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGetReading = async (e) => {
    e.preventDefault();
    
    if (!name || !name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (name.trim().length < 2) {
      toast.error('Please enter a valid name');
      return;
    }

    if (!birthday) {
      toast.error('Please select your birthday');
      return;
    }

    if (!gender) {
      toast.error('Please select your gender');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    const birthDate = new Date(birthday);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    
    if (birthDate > today) {
      toast.error('Birthday cannot be in the future');
      return;
    }

    if (age > 150) {
      toast.error('Please enter a valid birthday');
      return;
    }

    setLoading(true);
    try {
      toast.success('Channeling cosmic energy... This may take 30-45 seconds.');
      
      const response = await axios.post(`${API}/generate-reading`, {
        name: name.trim(),
        birthday: birthday,
        gender: gender
      }, {
        timeout: 120000
      });

      if (!response.data || !response.data.reading_id) {
        throw new Error('Failed to generate reading');
      }

      toast.success('Your reading is ready!');
      
      setTimeout(() => {
        window.location.href = `/reading/${response.data.reading_id}`;
      }, 1000);
    } catch (error) {
      console.error('Reading generation error:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Reading generation is taking longer than expected. Please try again in a moment.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`${errorMsg}. Please try again.`);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-void text-text-primary">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1589138489665-a887db6105e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxldGhlcmVhbCUyMG5lYnVsYSUyMGdvbGQlMjBkdXN0fGVufDB8fHx8MTc2NTMzODYzNHww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-void" />
        
        <div className="relative px-6 md:px-12 lg:px-24 pt-24 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-gold/30 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-sm text-text-secondary">AI-Powered Numerology</span>
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Discover Your
              <span className="block text-gold mt-2">Cosmic Blueprint</span>
            </h1>
            
            <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto mb-12">
              Unlock the ancient wisdom of numerology combined with AI to reveal your life purpose, 
              soul contract, and hidden gifts encoded in your name and birthday.
            </p>

            {/* Input Form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              onSubmit={handleGetReading}
              className="max-w-md mx-auto space-y-6"
              data-testid="numerology-input-form"
            >
              <div className="space-y-2">
                <Label htmlFor="name" className="text-text-primary text-left block">Your Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/5 border-white/10 focus:border-gold text-white h-14 px-6 rounded-none focus:ring-1 focus:ring-gold placeholder:text-white/20"
                  data-testid="name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthday" className="text-text-primary text-left block">Your Birthday</Label>
                <Input
                  id="birthday"
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  className="bg-white/5 border-white/10 focus:border-gold text-white h-14 px-6 rounded-none focus:ring-1 focus:ring-gold"
                  data-testid="birthday-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender" className="text-text-primary text-left block">Your Gender</Label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 focus:border-gold text-white h-14 px-6 rounded-none focus:ring-1 focus:ring-gold"
                  data-testid="gender-select"
                >
                  <option value="" className="bg-void-paper">Select Gender</option>
                  <option value="male" className="bg-void-paper">Male</option>
                  <option value="female" className="bg-void-paper">Female</option>
                </select>
              </div>

              <div className="flex items-start space-x-3 pt-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 border-2 border-white/20 rounded bg-white/5 checked:bg-gold focus:ring-2 focus:ring-gold cursor-pointer"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-text-secondary text-sm leading-relaxed cursor-pointer">
                  I acknowledge that this numerology reading is for <span className="text-gold">educational and entertainment purposes only</span>. 
                  I understand that I am solely responsible for all my actions and outcomes based on my decisions. 
                  The insights provided are not professional advice.
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-void hover:bg-gold-hover rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] h-14"
                data-testid="get-reading-button"
              >
                {loading ? (
                  <span>Generating Your Reading...</span>
                ) : (
                  <span>Get Your Free Reading</span>
                )}
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="px-6 md:px-12 lg:px-24 py-24">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-center mb-16">
            What Your Reading Reveals
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Compass, title: 'Life Purpose', desc: 'Your soul\'s mission and what you\'re here to accomplish' },
              { icon: Star, title: 'Soul Contract', desc: 'The agreements your soul made before incarnating' },
              { icon: Heart, title: 'Love Language', desc: 'How you give and receive love based on your numbers' },
              { icon: DollarSign, title: 'Money Code', desc: 'Your unique relationship with abundance and wealth' },
              { icon: Briefcase, title: 'Career Path', desc: 'Ideal career choices aligned with your energy' },
              { icon: Calendar, title: 'Best Days', desc: 'Most auspicious days for important decisions' },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-void-paper border border-white/5 p-8 hover:border-gold/30 transition-colors group"
              >
                <feature.icon className="w-8 h-8 text-gold mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-playfair text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-text-secondary text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* CTA Section */}
      <div className="px-6 md:px-12 lg:px-24 py-24 bg-void-paper">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6">
            Ready to Unlock Your Destiny?
          </h2>
          <p className="text-text-secondary mb-8">
            Join thousands who have discovered their true path through the power of numerology.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-gold text-void hover:bg-gold-hover rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]"
            data-testid="scroll-to-top-button"
          >
            Start Your Journey
          </Button>
        </motion.div>
      </div>

      {/* Footer Disclaimer */}
      <footer className="bg-void border-t border-white/5 px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-text-secondary text-sm space-y-3">
            <p className="font-medium text-gold">
              Terms & Conditions
            </p>
            <p className="leading-relaxed">
              <strong className="text-text-primary">Educational Purposes Only:</strong> This numerology application is designed for educational and entertainment purposes. 
              The readings, predictions, and insights provided are based on numerological principles, Feng Shui, and astrological traditions.
            </p>
            <p className="leading-relaxed">
              <strong className="text-text-primary">Personal Responsibility:</strong> You acknowledge that you perform your own actions and are solely responsible for all outcomes based on your decisions. 
              The information provided should not be considered professional advice (financial, legal, medical, or otherwise).
            </p>
            <p className="leading-relaxed">
              <strong className="text-text-primary">No Guarantees:</strong> While we strive for accuracy, we make no guarantees about the completeness, reliability, or accuracy of the information provided. 
              Use this tool as a guide for self-reflection and personal growth.
            </p>
            <p className="text-xs text-text-muted pt-4 border-t border-white/5">
              © 2024 Numerology AI. All rights reserved. By using this service, you agree to these terms.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
